import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import AuthContext from "../../context/authProvider";
import Loading from "../atoms/loading";
import { getArtLabels } from "./functions";
import { statusLabel } from "./status";

const DEBOUNCE_MS = 300;

export const STATUSES = ["draft", "queued", "generating", "review", "approved", "rejected", "failed"];

const countAll = (counts) => Object.values(counts || {}).reduce((sum, n) => sum + (Number(n) || 0), 0);

const statusClass = (s) => ({
    review: "bg-[#d59106] text-black", approved: "bg-green-600 text-black", failed: "bg-red-600 text-black",
    generating: "bg-blue-500 text-black animate-pulse", queued: "bg-blue-900",
}[s] || "bg-gray-700");

const ArtLabelsLibrary = () => {
    const { user } = useContext(AuthContext);
    const navigate = useNavigate();
    const [status, setStatus] = useState("");
    const [qInput, setQInput] = useState("");
    const [setInput, setSetInput] = useState("");
    const [q, setQ] = useState("");
    const [set, setSet] = useState("");
    const [data, setData] = useState({ items: [], counts: {}, total: 0 });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const seqRef = useRef(0);
    const aliveRef = useRef(true);

    useEffect(() => {
        aliveRef.current = true;
        return () => {
            aliveRef.current = false;
        };
    }, []);

    useEffect(() => {
        const timer = setTimeout(() => setQ(qInput), DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [qInput]);

    useEffect(() => {
        const timer = setTimeout(() => setSet(setInput), DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [setInput]);

    const load = useCallback(async () => {
        setLoading(true);
        const mySeq = ++seqRef.current;
        const out = await getArtLabels(user?.token, { status, q, set });
        if (!aliveRef.current || seqRef.current !== mySeq) return; // resposta obsoleta ou componente desmontado
        setLoading(false);
        if (out?.type === "success") { setData(out.data); setError(""); } else { setError(out?.message || "Falha ao listar"); }
    }, [user?.token, status, q, set]);

    useEffect(() => { load(); }, [load]);

    const goToArt = (artKey) => navigate(`/manafix/art-labels/${encodeURIComponent(artKey)}`);

    return (
        <div>
            <div className="flex flex-wrap gap-2 mb-3">
                <button type="button" aria-pressed={status === ""} onClick={() => setStatus("")} className={`px-2 ${status === "" ? "bg-[#d59106] text-black" : "border border-[#d59106]"}`}>todas ({countAll(data.counts)})</button>
                {STATUSES.map((s) => (
                    <button key={s} type="button" aria-pressed={status === s} onClick={() => setStatus(s)} className={`px-2 ${status === s ? "bg-[#d59106] text-black" : "border border-[#d59106]"}`}>
                        {statusLabel(s)} ({data.counts?.[s] || 0})
                    </button>
                ))}
            </div>
            <div className="flex gap-2 mb-3">
                <input className="text-black px-2" placeholder="Nome da carta" value={qInput} onChange={(e) => setQInput(e.target.value)} />
                <input className="text-black px-2" placeholder="Set (ex.: swsh3)" value={setInput} onChange={(e) => setSetInput(e.target.value)} />
            </div>
            {error ? <p className="text-red-400">{error}</p> : null}
            {loading ? <Loading /> : (
                <table className="w-full text-sm">
                    <thead className="text-[#d59106]">
                        <tr><th scope="col" className="text-left">Carta</th><th scope="col" className="text-left">Set</th><th scope="col">Frame</th><th scope="col">Status</th><th scope="col">Versão</th><th scope="col">Modelos</th><th scope="col">Atualizada</th></tr>
                    </thead>
                    <tbody>
                        {data.items.map((a) => (
                            <tr
                                key={a.artKey}
                                className="cursor-pointer hover:bg-[#1a1a1a] border-b border-[#333]"
                                tabIndex={0}
                                role="link"
                                aria-label={`Abrir arte ${a.cardName}`}
                                onClick={() => goToArt(a.artKey)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        goToArt(a.artKey);
                                    }
                                }}
                            >
                                <td>{a.cardName} <span className="text-gray-500 text-xs">{a.artKey}</span>{a.rules?.setMotif ? <span className="ml-1 text-[10px] bg-[#333] text-[#d59106] px-1">motivo do set</span> : null}</td>
                                <td>{a.setName || a.setId}</td>
                                <td className="text-center">{a.frameType}</td>
                                <td className="text-center"><span className={`px-2 ${statusClass(a.status)}`}>{statusLabel(a.status)}</span></td>
                                <td className="text-center">{a.approved?.version ? `v${a.approved.version}` : "—"}</td>
                                <td className="text-center">{(a.gradableModels || []).length}</td>
                                <td className="text-center">{a.updatedAt ? new Date(a.updatedAt).toLocaleString("pt-BR") : ""}</td>
                            </tr>
                        ))}
                        {!data.items.length ? <tr><td colSpan={7} className="text-center text-gray-500 py-4">Nenhuma arte neste filtro.</td></tr> : null}
                    </tbody>
                </table>
            )}
        </div>
    );
};

export default ArtLabelsLibrary;
